import React, { useEffect, useRef } from "react";
import * as THREE from "three";

export default function Galaxy3D() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 200);
    camera.position.set(0, 0, 9);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";

    // Starfield
    const starCount = 1500;
    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      const r = 30 + Math.random() * 60;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.09, transparent: true, opacity: 0.65, sizeAttenuation: true });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    // Dimensional structures — wireframe pipes / knots / rings at the edges
    const group = new THREE.Group();
    const structures = [];

    const addStructure = (mesh, pos, rot, floatSpeed, spinSpeed) => {
      mesh.position.set(...pos);
      mesh.rotation.set(...rot);
      group.add(mesh);
      structures.push({ mesh, baseY: pos[1], floatSpeed, spinSpeed, phase: Math.random() * Math.PI * 2 });
    };

    addStructure(
      new THREE.Mesh(
        new THREE.TorusKnotGeometry(1.5, 0.3, 160, 18),
        new THREE.MeshBasicMaterial({ color: 0x7b2ff7, wireframe: true, transparent: true, opacity: 0.2 })
      ),
      [5.4, 1.6, -6], [0.4, 0.2, 0], 0.5, 0.12
    );
    addStructure(
      new THREE.Mesh(
        new THREE.TorusGeometry(2.6, 0.045, 12, 90),
        new THREE.MeshBasicMaterial({ color: 0xff3d77, transparent: true, opacity: 0.3 })
      ),
      [-6.2, -2.4, -8], [1.2, 0.4, 0.3], 0.35, 0.08
    );
    addStructure(
      new THREE.Mesh(
        new THREE.TorusGeometry(1.7, 0.03, 10, 80),
        new THREE.MeshBasicMaterial({ color: 0xff8a00, transparent: true, opacity: 0.28 })
      ),
      [-5.6, 2.9, -9], [0.9, 0.2, 0.6], 0.3, 0.1
    );
    addStructure(
      new THREE.Mesh(
        new THREE.IcosahedronGeometry(1.3, 1),
        new THREE.MeshBasicMaterial({ color: 0x5ed6ff, wireframe: true, transparent: true, opacity: 0.13 })
      ),
      [6.6, -3.2, -10], [0.3, 0.8, 0.2], 0.45, 0.15
    );
    scene.add(group);

    const mouse = { x: 0, y: 0 };
    const onMove = (e) => {
      mouse.x = e.clientX / window.innerWidth - 0.5;
      mouse.y = e.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("mousemove", onMove);

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    resize();
    window.addEventListener("resize", resize);

    const clock = new THREE.Clock();
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (document.hidden) return;
      const t = clock.getElapsedTime();
      const scroll = window.scrollY * 0.00035;
      group.rotation.y = t * 0.03 + mouse.x * 0.16;
      group.rotation.x = mouse.y * 0.1 - scroll;
      stars.rotation.y = t * 0.008;
      stars.rotation.x = -scroll * 0.5;
      for (const s of structures) {
        s.mesh.position.y = s.baseY + Math.sin(t * s.floatSpeed + s.phase) * 0.45;
        s.mesh.rotation.y += s.spinSpeed * 0.01;
        s.mesh.rotation.x += s.spinSpeed * 0.006;
      }
      renderer.render(scene, camera);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
      });
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0" data-testid="galaxy-3d" />;
}
